import {
  Box,
  CircularProgress,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
  LinearProgress,
  Link,
} from "@mui/material";
import { useState, useMemo } from "react";
import * as yup from "yup";
import { useFormik } from "formik";
import { FilledButton } from "../../../../../../customComponents/styled/styledButtons";
import { useGetTeacherSubjectsQuery } from "../../../../../../app/questionApiSlice";
import { useUploadFileMutation } from "../../../../../../app/utilsApiSlice";
import {
  useCreateBulkQuestionMutation,
} from "../../../../../../app/questionApiSlice";
import { CloudUpload } from "@mui/icons-material";

const BulkQuestion = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const { data: classrooms, isLoading: isFetchingClassrooms } =
    useGetTeacherSubjectsQuery();

  const [uploadFile] = useUploadFileMutation();
  const [createBulkQuestion, { isLoading: isCreatingBulkQuestion }] =
    useCreateBulkQuestionMutation();

  // Get unique subjects the teacher teaches
  const availableSubjects = useMemo(() => {
    if (!classrooms) return [];
    const subjectMap = new Map();
    classrooms.forEach((subject) => {
      if (!subjectMap.has(subject.name)) {
        subjectMap.set(subject.name, subject);
      }
    });
    return Array.from(subjectMap.values());
  }, [classrooms]);

  const formik = useFormik({
    initialValues: {
      subject: "",
      level: "",
      topic: "",
      bank: "private",
    },
    validationSchema: yup.object({
      subject: yup.string().required("Required"),
      level: yup.string().required("Required"),
      topic: yup.string().required("Required"),
    }),
    onSubmit: async (values) => {
      if (!selectedFile) {
        setUploadError("Please select a file to upload");
        return;
      }

      try {
        setIsProcessing(true);
        setUploadError(null);

        // Upload the file using RTK Query mutation
        const formData = new FormData();
        formData.append("file", selectedFile);

        const uploadResponse = await uploadFile(formData).unwrap();

        if (!uploadResponse.url) {
          throw new Error("Failed to upload file");
        }

        // Create bulk question with the uploaded document URL
        await createBulkQuestion({
          subject: values.subject,
          level: values.level,
          topic: values.topic,
          bank: values.bank,
          document_url: uploadResponse.url,
        }).unwrap();

        setUploadSuccess(true);
        setSelectedFile(null);
        formik.resetForm();
        setTimeout(() => setUploadSuccess(false), 3000);
      } catch (error) {
        console.error(error);
        setUploadError(
          error?.message || "Failed to upload questions. Please try again."
        );
      } finally {
        setIsProcessing(false);
      }
    },
  });

  // Get levels that teach the selected subject
  const availableLevels = useMemo(() => {
    if (!formik.values.subject || !classrooms) return [];
    const levelMap = new Map();
    classrooms.forEach((subject) => {
      if (subject.name === formik.values.subject) {
        if (!levelMap.has(subject.level)) {
          levelMap.set(subject.level, subject.level);
        }
      }
    });
    return Array.from(levelMap.values());
  }, [formik.values.subject, classrooms]);


  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type (accept .docx and .doc)
      if (
        !file.type.includes(
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ) &&
        !file.type.includes("application/msword")
      ) {
        setUploadError("Please upload a Word document (.doc or .docx)");
        return;
      }
      setSelectedFile(file);
      setUploadError(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const event = {
        target: { files: [file] },
      };
      handleFileChange(event);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontSize: "1.25rem", fontWeight: 600 }}>
        Bulk Questions
      </Typography>
      <Box sx={{ marginBottom: "2em" }}>
        <Typography sx={{ marginBottom: ".5em", color: "#666" }}>
          Upload multiple questions from a Word document. Format your document with: bold text for questions, italic for options, underlined for correct answers, and bold+italic for explanations.
        </Typography>
        <Link
          href="/questions-bulk-upload-sample.docx"
          download="questions-bulk-upload-sample.docx"
          sx={{
            textDecoration: "none",
            color: "primary.safeOnLight",
            fontWeight: 500,
            fontSize: "0.95rem",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          Download sample document
        </Link>
      </Box>

      {uploadSuccess && (
        <Alert severity="success" sx={{ marginBottom: "1rem" }}>
          Questions have been uploaded successfully!
        </Alert>
      )}

      {uploadError && (
        <Alert severity="error" sx={{ marginBottom: "1rem" }}>
          {uploadError}
        </Alert>
      )}

      <Box component="form" onSubmit={formik.handleSubmit}>
        <Grid container spacing={3} sx={{ marginBottom: "2em" }}>
          <Grid item xs={12} md={4}>
            <Box sx={{ width: "100%" }}>
              <InputLabel
                sx={{
                  fontSize: ".75rem",
                  fontWeight: 400,
                  color: "#000000",
                  marginBottom: ".5em",
                }}
              >
                SUBJECT
              </InputLabel>
              <Select
                name="subject"
                value={formik.values.subject}
                onChange={(e) => {
                  formik.setFieldValue("subject", e.target.value);
                  formik.setFieldValue("level", "");
                  formik.setFieldValue("topic", "");
                }}
                placeholder="Subject"
                fullWidth
                sx={{
                  backgroundColor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#F9F9F9",
                  borderRadius: "12px",
                  cursor: isFetchingClassrooms ? "progress" : "pointer",
                }}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select Subject
                </MenuItem>
                {availableSubjects?.map((subject) => (
                  <MenuItem value={subject.name} key={subject.id}>
                    {subject.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ width: "100%" }}>
              <InputLabel
                sx={{
                  fontSize: ".75rem",
                  fontWeight: 400,
                  color: "#000000",
                  marginBottom: ".5em",
                }}
              >
                LEVEL
              </InputLabel>
              <Select
                name="level"
                value={formik.values.level}
                onChange={(e) => {
                  formik.setFieldValue("level", e.target.value);
                  formik.setFieldValue("topic", "");
                }}
                placeholder="Level"
                fullWidth
                sx={{
                  backgroundColor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#F9F9F9",
                  borderRadius: "12px",
                  cursor: isFetchingClassrooms ? "progress" : "pointer",
                  textTransform: "capitalize",
                }}
                displayEmpty
                disabled={!formik.values.subject}
              >
                <MenuItem value="" disabled>
                  {formik.values.subject ? "Select Level" : "Select Subject First"}
                </MenuItem>
                {availableLevels?.map((level) => (
                  <MenuItem
                    value={level}
                    key={level}
                    sx={{ textTransform: "capitalize" }}
                  >
                    {level}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ width: "100%" }}>
              <InputLabel
                sx={{
                  fontSize: ".75rem",
                  fontWeight: 400,
                  color: "#000000",
                  marginBottom: ".5em",
                }}
              >
                TOPIC
              </InputLabel>
              <TextField
                name="topic"
                value={formik.values.topic}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter topic"
                fullWidth
                error={formik.touched.topic && Boolean(formik.errors.topic)}
                helperText={formik.touched.topic && formik.errors.topic}
                disabled={!formik.values.level}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#F9F9F9",
                    borderRadius: "12px",
                  },
                }}
              />
            </Box>
          </Grid>
        </Grid>

        {/* File Upload Dropzone */}
        <Box
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          sx={{
            borderRadius: "12px",
            border: "2px dashed #ccc",
            backgroundColor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#F9F9F9",
            padding: "3em",
            textAlign: "center",
            cursor: "pointer",
            transition: "all 0.3s ease",
            marginBottom: "2em",
            "&:hover": {
              borderColor: "#1976d2",
              backgroundColor: "#f0f7ff",
            },
          }}
        >
          <input
            type="file"
            accept=".doc,.docx"
            onChange={handleFileChange}
            style={{ display: "none" }}
            id="file-input"
            disabled={isProcessing}
          />
          <label
            htmlFor="file-input"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              cursor: isProcessing ? "not-allowed" : "pointer",
              opacity: isProcessing ? 0.6 : 1,
            }}
          >
            <CloudUpload
              sx={{
                fontSize: "3em",
                color: "primary.safeOnLight",
                marginBottom: "1em",
              }}
            />
            <Typography sx={{ fontWeight: 600, marginBottom: ".5em" }}>
              {selectedFile ? selectedFile.name : "Drag and drop your Word document here"}
            </Typography>
            <Typography sx={{ fontSize: "0.9rem", color: "#666" }}>
              or click to select a file
            </Typography>
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "#999",
                marginTop: "1em",
              }}
            >
              Supported formats: .doc, .docx
            </Typography>
          </label>
        </Box>

        {isProcessing && <LinearProgress sx={{ marginBottom: "1em" }} />}

        <Box sx={{ display: "flex", gap: "1em" }}>
          <FilledButton
            type="submit"
            disabled={
              !formik.values.subject ||
              !formik.values.level ||
              !formik.values.topic ||
              !selectedFile ||
              isProcessing ||
              isCreatingBulkQuestion
            }
            sx={{
              opacity:
                !formik.values.subject ||
                !formik.values.level ||
                !formik.values.topic ||
                !selectedFile ||
                isProcessing ||
                isCreatingBulkQuestion
                  ? 0.5
                  : 1,
            }}
          >
            {isProcessing || isCreatingBulkQuestion ? (
              <>
                <CircularProgress size={20} sx={{ marginRight: "0.5em" }} />
                Uploading...
              </>
            ) : (
              "Upload Questions"
            )}
          </FilledButton>
        </Box>
      </Box>
    </Box>
  );
};

export default BulkQuestion;
